import { CustomerSelect } from "@/components/data-driven-inputs/selects/customer-select/CustomerSelect";
import { GapValues } from "@/lib/utils/GapValues";
import { ZIndex } from "@/lib/utils/ZIndexEnum";
import { CSSObject } from "@emotion/react";
import { Box, Column, Row } from "@stenajs-webui/core";
import {
  Banner,
  Label,
  PrimaryButton,
  SecondaryButton,
} from "@stenajs-webui/elements";
import { DateTextInput } from "@stenajs-webui/calendar";
import { Window, WindowFooter } from "@stenajs-webui/modal";
import * as React from "react";
import { useState } from "react";
import { formatDateString } from "@/lib/dates/formatters";
import { effectText } from "../lib/parentChangeText";
import {
  dayAfterLastParentEnds,
  openEndedStretchError,
  parentSpanError,
} from "../lib/parentDateRules";
import { ParentHistoryEntry, ParentChange } from "../types";

/**
 * Where the date starts when the history gives nothing to follow. Changes
 * are dated forward, and the bulk of them are made at the start of a
 * negotiation, so the next turn of the year is the date most of them want.
 */
const firstOfNextYear = (): string => `${new Date().getFullYear() + 1}-01-01`;

interface Props {
  history: ParentHistoryEntry[];
  /** Settles once the write and the read after it are done. */
  onSave: (change: ParentChange) => Promise<unknown>;
  onRequestClose: () => void;
}

export const CustomerParentEditorModal: React.FC<Props> = ({
  history,
  onSave,
  onRequestClose,
}) => {
  const [parentCustomerNo, setParentCustomerNo] = useState("");
  /**
   * Picked up where the history leaves off, so the common case - one parent
   * following another - needs no typing. Still editable: any date is
   * allowed as long as it does not clash, and the banner below says when it
   * does.
   */
  const [validFrom, setValidFrom] = useState(
    () => dayAfterLastParentEnds(history) ?? firstOfNextYear()
  );
  /**
   * Held here rather than taken from the mutation, so the spinner belongs
   * to the button that was pressed. onSave closes the editor when it is
   * done, so this is never turned back off - the component goes with it.
   */
  const [saving, setSaving] = useState(false);

  /**
   * A new parent runs on with no end, so its span is open-ended - which
   * is why an existing open end has to be closed before anything can be
   * added. That is said as soon as the editor opens, since no choice of
   * date gets around it.
   */
  const error =
    openEndedStretchError(history) ??
    parentSpanError(history, { validFrom, validTo: null });
  const canSave = !!validFrom && !error && !!parentCustomerNo;

  return (
    <Window
      isOpen
      onRequestClose={onRequestClose}
      headerText={"Add parent"}
      width={"460px"}
      indent={GapValues.TWENTYFOUR}
    >
      <Column gap={GapValues.SIXTEEN}>
        <Label text={"New parent account"}>
          <CustomerSelect
            placeholder={"Customer no. or index"}
            onValueChange={(customer) =>
              setParentCustomerNo(String(customer.custNo))
            }
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base: CSSObject) => ({
                ...base,
                zIndex: ZIndex.highest,
              }),
            }}
          />
        </Label>

        <Box width={"220px"}>
          <Label text={"Valid from"}>
            <DateTextInput
              value={validFrom}
              onValueChange={setValidFrom}
              portalTarget={document.body}
              zIndex={ZIndex.highest}
            />
          </Label>
        </Box>

        <Banner
          variant={error ? "error" : "info"}
          text={
            error ??
            effectText({
              history,
              today: formatDateString(new Date()),
              newParentNo: parentCustomerNo,
              validFrom,
            })
          }
        />
      </Column>
      <WindowFooter>
        <Row gap={GapValues.SIXTEEN} justifyContent={"flex-end"}>
          <SecondaryButton
            label={"Cancel"}
            disabled={saving}
            onClick={onRequestClose}
          />
          <PrimaryButton
            label={"Save"}
            loading={saving}
            loadingLabel={"Saving"}
            disabled={!canSave}
            onClick={() => {
              setSaving(true);
              onSave({ parentCustomerNo, validFrom });
            }}
          />
        </Row>
      </WindowFooter>
    </Window>
  );
};
